/**
 * Micro Practices slice
 */

import { createAction, createSlice } from '@reduxjs/toolkit';
import { MicroPractice } from '../../store/slices/types';

const initialState = {
  microPractices: [] as MicroPractice[],
  currentMicroPractice: null as MicroPractice | null,
  loading: false,
  error: null as string | null,
  pagination: {
    total: 0,
    page: 1,
    limit: 10,
  },
};

const microPracticesSlice = createSlice({
  name: 'microPractices',
  initialState,
  reducers: {
    setMicroPractices(state, action) {
      // Handle both array response and paginated response
      if (Array.isArray(action.payload)) {
        state.microPractices = action.payload;
      } else if (action.payload?.data) {
        state.microPractices = action.payload.data;
        if (action.payload.pagination) {
          state.pagination = {
            total: action.payload.pagination.total || action.payload.total || 0,
            page: action.payload.pagination.page || action.payload.page || 1,
            limit: action.payload.pagination.limit || action.payload.limit || 10,
          };
        }
      } else {
        state.microPractices = [];
      }
      state.loading = false;
      state.error = null;
    },
    setCurrentMicroPractice(state, action) {
      state.currentMicroPractice = action.payload;
      state.loading = false;
      state.error = null;
    },
    addMicroPractice(state, action) {
      state.microPractices.push(action.payload);
      state.loading = false;
      state.error = null;
    },
    updateMicroPractice(state, action) {
      const index = state.microPractices.findIndex(
        (mp) => (mp._id || mp.id) === (action.payload._id || action.payload.id)
      );
      if (index !== -1) {
        state.microPractices[index] = action.payload;
      }
      if (state.currentMicroPractice && (state.currentMicroPractice._id || state.currentMicroPractice.id) === (action.payload._id || action.payload.id)) {
        state.currentMicroPractice = action.payload;
      }
      state.loading = false;
      state.error = null;
    },
    removeMicroPractice(state, action) {
      state.microPractices = state.microPractices.filter(
        (mp) => (mp._id || mp.id) !== action.payload
      );
      state.loading = false;
      state.error = null;
    },
    setLoading(state, action) {
      state.loading = action.payload;
    },
    setError(state, action) {
      state.error = action.payload;
      state.loading = false;
    },
    clearError(state) {
      state.error = null;
    },
  },
});

// Reducer
export const microPracticesReducer = microPracticesSlice.reducer;

// Actions
export const {
  setMicroPractices,
  setCurrentMicroPractice,
  addMicroPractice,
  updateMicroPractice,
  removeMicroPractice,
  setLoading,
  setError,
  clearError,
} = microPracticesSlice.actions;

// Async Actions
export const fetchMicroPractices = createAction<{ page?: number; limit?: number; activeOnly?: boolean } | undefined>('MICRO_PRACTICES/FETCH_ALL');
export const fetchMicroPractice = createAction<string>('MICRO_PRACTICES/FETCH_ONE');
export const createMicroPractice = createAction<Omit<MicroPractice, '_id' | 'id'>>('MICRO_PRACTICES/CREATE');
export const updateMicroPracticeAction = createAction<{ id: string; data: Partial<MicroPractice> }>('MICRO_PRACTICES/UPDATE');
export const deleteMicroPractice = createAction<string>('MICRO_PRACTICES/DELETE');

